import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect } from 'react'
import { I18n } from 'common'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import { SimpleModal } from '../../../ui-components/modals/SimpleModal'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd'
import styled from 'styled-components'
import {
  SEARCH_PREFERENCE_VERSION,
  useCustomizeSearchStore,
} from '../../../store/customiseSearch/CustomizeSearchStore'
import { SearchPreferenceItem } from './SearchPreferenceItem'
import { SearchPreference } from '../interfaces'
import { StoreInstances } from '../../../store/StoreInstancesContainer'
import {
  MiscPreferencesKeys,
  MutablePreference,
  useUserStore,
} from '../../../store/user/UserStore'
import VehicleTypeDropdown from './VehicleTypeDropdown'
import { H6, Text } from './Common'
import { sortVehicleTypes } from '../../../store/customiseSearch/Utils'

export const CustomizeSearchModal = observer((): ReactElement => {
  const { showCustomizeSearchModal, setShowCustomizeSearchModal } =
    useUiStateStore()
  const {
    copyOfSearchPreferences,
    getVisibleSearchPreferences,
    getHiddenSearchPreferences,
    setSearchPreferences,
    setCopyOfSearchPreferences,
    resetCopy,
    setSelectedTab,
    getVehicleTypePreference,
    getVehicleTypes,
    setVehicleTypes,
  } = useCustomizeSearchStore()

  const {
    updateUserPreferenceAndSyncToCloud,
    preferences: accountPreferences,
    updateMiscPreferences,
  } = useUserStore()

  const vehicleTypes = getVehicleTypes()

  useEffect(() => {
    const userAccountSearchPreferencesStr =
      accountPreferences?.searchPreferences
    const version = accountPreferences?.searchPreferencesVersion
    const isPlateSearchEnabled =
      accountPreferences.epeFeatures_plateToVin === 'true'

    if (
      userAccountSearchPreferencesStr &&
      version === SEARCH_PREFERENCE_VERSION
    ) {
      const userAccountSearchPreferences: SearchPreference[] = JSON.parse(
        userAccountSearchPreferencesStr
      )
      setSearchPreferences(userAccountSearchPreferences)
      if (isPlateSearchEnabled) {
        // Add it in case it is not in the user cache already
        StoreInstances.customizeSearchStore.addLicensePlateOption()
      }

      setSelectedTab(userAccountSearchPreferences[0].id)
    }

    if (!isPlateSearchEnabled) {
      StoreInstances.customizeSearchStore.removeLicensePlateOption()
    }
  }, [accountPreferences, setSearchPreferences, setSelectedTab])

  let visibleSearchPreferences = getVisibleSearchPreferences()
  const hiddenSearchPreferences = getHiddenSearchPreferences()

  const handleOnConfirm = (): void => {
    setShowCustomizeSearchModal(false)
    const allPreferences = visibleSearchPreferences
    allPreferences.push(...hiddenSearchPreferences)
    setSearchPreferences(allPreferences)

    updateUserPreferenceAndSyncToCloud(
      MutablePreference.SEARCH_PREFERENCE,
      JSON.stringify(allPreferences)
    )
    updateUserPreferenceAndSyncToCloud(
      MutablePreference.SEARCH_PREFERENCE_VERSION,
      SEARCH_PREFERENCE_VERSION
    )

    const preference = getVehicleTypePreference()
    const sortedVehicleTypes = sortVehicleTypes(vehicleTypes, preference)
    setVehicleTypes(sortedVehicleTypes)

    StoreInstances.searchStore.setCurrentFieldsSelections({
      type: preference,
      year: undefined,
      make: undefined,
      model: undefined,
      engine: undefined,
    })
    const prefAttribute = {
      [MiscPreferencesKeys.DEFAULT_VEHICLE_TYPE]: JSON.stringify(preference),
    }
    updateMiscPreferences(prefAttribute)
  }

  const handleOnCancel = (): void => {
    setShowCustomizeSearchModal(false)
    resetCopy()
  }

  const handleOnHideElement = (item: SearchPreference): void => {
    const items = Array.from(copyOfSearchPreferences)
    const newArr = items.map((sp) => {
      if (sp.id === item.id) return { ...sp, hidden: !item.hidden }
      return sp
    })
    setCopyOfSearchPreferences(newArr)
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination) return

    const items = Array.from(visibleSearchPreferences)
    const [newOrder] = items.splice(source.index, 1)
    items.splice(destination.index, 0, newOrder)
    visibleSearchPreferences = items
  }

  return (
    <SimpleModal
      title="customizeSearchPreferences"
      show={showCustomizeSearchModal}
      onSecondaryAction={handleOnCancel}
      onPrimaryAction={handleOnConfirm}
      secondaryActionText="cancel"
      primaryActionText="savePreferences"
      borderBottomHeader
      borderTopFooter
    >
      <Body>
        <Indications>
          <H6>
            <I18n name="searchByTabOrder" />
          </H6>
          <Text>
            <I18n name="dragAndRearrangeTheItemsInThisListToArrangeThemInYourPreferredDisplayOrder" />
            . <I18n name="yourFirstItemWillBeYourDefault" />
            . <I18n name="toHideAnItemClickTheEyeIcon" />.
          </Text>
        </Indications>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="search_preferences">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {visibleSearchPreferences.map((sp, index) => {
                  return (
                    <Draggable key={sp.id} draggableId={sp.id} index={index}>
                      {(provided2) => (
                        <div
                          ref={provided2.innerRef}
                          {...provided2.draggableProps}
                          {...provided2.dragHandleProps}
                        >
                          <SearchPreferenceItem
                            key={`visible_${sp.id}`}
                            item={sp}
                            index={index + 1}
                            onHideElement={handleOnHideElement}
                          />
                        </div>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div>
          {hiddenSearchPreferences.map((sp, idx) => (
            <SearchPreferenceItem
              key={`hidden_${sp.id}`}
              item={sp}
              index={visibleSearchPreferences?.length + idx + 1}
              onHideElement={handleOnHideElement}
            />
          ))}
        </div>
        <HiddenHeader>
          <HorizontalLine />
        </HiddenHeader>
        <VehicleTypeDropdown
          options={vehicleTypes}
          onChange={(v) =>
            StoreInstances.customizeSearchStore.setVehicleTypePreference(v)
          }
        />
      </Body>
    </SimpleModal>
  )
})

const Indications = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 1.5rem;
`

const HiddenHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #adadaa;
  margin: 24px 0px;
`

const HorizontalLine = styled.div`
  display: flex;
  justify-content: center;
  border-bottom: 1px solid;
  flex-grow: 1;
`

const Body = styled.div`
  padding-top: 1rem;
  min-height: 557px;
`
